/* Notepad Window */
.notepad {
  position: absolute;
  background: var(--color-surface);
  border: var(--border-medium) var(--border-style) var(--color-border-strong);
  border-radius: var(--radius-window);
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  will-change: transform;
  display: flex;
  flex-direction: column;
  animation: windowOpen 0.3s ease-out;
  transform-origin: center center;
}

.notepad.dragging {
  cursor: grabbing;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
}

.notepad.maximized {
  border-radius: 0;
}

@keyframes windowOpen {
  from {
    opacity: 0;
    scale: 0.95;
  }
  to {
    opacity: 1;
    scale: 1;
  }
}

.notepad.closing {
  animation: windowClose 0.2s ease-in forwards;
  pointer-events: none;
}

@keyframes windowClose {
  from {
    opacity: 1;
    scale: 1;
  }
  to {
    opacity: 0;
    scale: 0.95;
  }
}

.notepadHeader {
  background: var(--color-surface-raised);
  border-bottom: var(--border-thin) var(--border-style) var(--color-border);
  padding: var(--space-2) var(--space-3);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: grab;
  user-select: none;
  touch-action: none;
}

.notepad.dragging .notepadHeader {
  cursor: grabbing;
}

.notepadTitle {
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text-muted);
  margin-left: auto;
}

.notepadMenuBar {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  background: var(--color-surface-sunken);
  border-bottom: var(--border-thin) var(--border-style) var(--color-border);
}

.notepadMenuButton {
  padding: var(--space-1) var(--space-2);
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text-muted);
  background: transparent;
  border: none;
  border-radius: var(--radius-xs);
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
}

.notepadMenuButton:hover:not(:disabled) {
  background: var(--color-surface);
  color: var(--color-text);
}

.notepadMenuButton:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.notepadBody {
  flex: 1;
  overflow: hidden;
  display: flex;
}

.notepadTextarea {
  flex: 1;
  padding: var(--space-3);
  font-family: var(--font-mono);
  font-size: var(--body-size);
  line-height: 1.6;
  color: var(--color-text);
  background: transparent;
  border: none;
  outline: none;
  resize: none;
}

.notepadTextarea::placeholder {
  color: var(--color-text-subtle);
}

.notepadTextarea::selection {
  background: var(--color-accent-primary);
  color: white;
}

.notepadStatusBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-1) var(--space-3);
  background: var(--color-surface-sunken);
  border-top: var(--border-thin) var(--border-style) var(--color-border);
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-text-subtle);
}

/* Notepad Save Dialog */
.notepadSaveDialog {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.saveDialogContent {
  background: var(--color-surface);
  border: var(--border-medium) var(--border-style) var(--color-border-strong);
  border-radius: var(--radius-sm);
  padding: var(--space-4);
  min-width: 280px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.saveDialogTitle {
  font-family: var(--font-mono);
  font-size: var(--body-size);
  font-weight: var(--font-semibold);
  color: var(--color-text);
  margin-bottom: var(--space-3);
}

.saveDialogField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-bottom: var(--space-2);
}

.saveDialogLabel {
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text-muted);
}

.saveDialogInput {
  padding: var(--space-2);
  font-family: var(--font-mono);
  font-size: var(--body-size);
  color: var(--color-text);
  background: var(--color-surface-sunken);
  border: var(--border-thin) var(--border-style) var(--color-border);
  border-radius: var(--radius-xs);
  outline: none;
}

.saveDialogInput:focus {
  border-color: var(--color-accent-primary);
}

.saveDialogInput::placeholder {
  color: var(--color-text-subtle);
}

.saveDialogLocation {
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text-subtle);
  margin-bottom: var(--space-3);
}

.saveDialogError {
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-error, #ff6b6b);
  margin-bottom: var(--space-2);
}

.saveDialogButtons {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

.saveDialogButton {
  padding: var(--space-1) var(--space-3);
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text);
  background: var(--color-surface-sunken);
  border: var(--border-thin) var(--border-style) var(--color-border);
  border-radius: var(--radius-xs);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.saveDialogButton:hover {
  background: var(--color-surface-raised);
}

.saveDialogButton.primary {
  background: var(--color-accent-primary);
  border-color: var(--color-accent-primary);
  color: white;
}

.saveDialogButton.primary:hover {
  opacity: 0.9;
}
